// src/components/Markers/PipelineLayer.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './MapMarkers.css'; // стили для боковой панели
import API_BASE_URL from '../../config';

export default function PipelineLayer() {
  const map = useMap().current;
  const navigate = useNavigate();

  // 1) Состояния для данных
  const [collectors, setCollectors] = useState([]);       // все коллекторы
  const [riskItems, setRiskItems] = useState([]);         // массив { id, name, risk_score, priority }
  const [designs, setDesigns] = useState([]);             // массив конструктивных решений

  // 2) Hover/selected для линий
  const hoveredCollectorId = useRef(null);
  const selectedCollectorId = useRef(null);

  // 3) Выбранный коллектор для боковой панели
  const [selectedCollector, setSelectedCollector] = useState(null);
  const [animatePanel, setAnimatePanel] = useState(false);

  // ──────────────────────────────────────────────────────────
  // Загрузка «collectors», «riskItems» и «designs»
  // ──────────────────────────────────────────────────────────

  useEffect(() => {
    // 1.1: Получаем все коллекторы
    axios.get(`${API_BASE_URL}/collector/get_all_collectors`)
      .then(res => setCollectors(res.data || []))
      .catch(err => console.error('Ошибка при получении всех коллекторов:', err));

    // 1.2: Получаем топ-100 рисков (risk_score + priority + name)
    axios.get(`${API_BASE_URL}/v1/risk/top?limit=100`)
      .then(res => setRiskItems(res.data || []))
      .catch(err => console.error('Ошибка при получении /v1/risk/top:', err));

    // 1.3: Получаем все конструктивные решения
    axios.get(`${API_BASE_URL}/collector/get_all_design`)
      .then(res => setDesigns(res.data || []))
      .catch(err => console.error('Ошибка при получении конструктивных решений:', err));
  }, []);

  // ──────────────────────────────────────────────────────────
  // Мерджим collectors + riskItems → добавляем { risk_score, priority, nameToShow }
  // ──────────────────────────────────────────────────────────

  const collectorsWithRisk = useMemo(() => {
    if (!collectors.length) return [];

    const riskMap = riskItems.reduce((acc, r) => {
      acc[r.id] = {
        risk_score: r.risk_score,
        priority: r.priority,
        nameFromRisk: r.name
      };
      return acc;
    }, {});

    return collectors.map(c => {
      const info = riskMap[c.id] || {};
      return {
        ...c,
        risk_score: info.risk_score !== undefined ? info.risk_score : 0,
        priority: info.priority !== undefined ? info.priority : 0,
        nameToShow: info.nameFromRisk || c.name
      };
    });
  }, [collectors, riskItems]);

  // ──────────────────────────────────────────────────────────
  // Строим GeoJSON для слоя «priority-segments»
  // ──────────────────────────────────────────────────────────

  const lineData = useMemo(() => {
    const features = collectorsWithRisk.map(c => {
      const latGroups = c.latitude.split('#');
      const lngGroups = c.longitude.split('#');
      const segments = latGroups.map((latGroup, i) => {
        const latNums = latGroup.split(';').map(Number);
        const lngNums = (lngGroups[i] || '').split(';').map(Number);
        return latNums.map((lat, j) => [lngNums[j], lat]);
      });
      return {
        type: 'Feature',
        id: c.id,
        properties: {
          name: c.nameToShow,
          risk_score: c.risk_score,
          priority: c.priority,
          collector_design_id: c.collector_design_id,
          material: c.material,
          length: c.length,
          expenditure: c.expenditure
        },
        geometry: {
          type: 'MultiLineString',
          coordinates: segments
        }
      };
    });
    return {
      type: 'FeatureCollection',
      features
    };
  }, [collectorsWithRisk]);

  // ──────────────────────────────────────────────────────────
  // Цветовые функции для риска
  // ──────────────────────────────────────────────────────────

  const getRiskColor = (score) => {
    if (score >= 0.8) return '#FF4C4C';
    if (score >= 0.6) return '#FFB84C';
    if (score >= 0.4) return '#FFD54C';
    if (score >= 0.2) return '#B2FF4C';
    return '#4CFF4C';
  };

  const getDarkRiskColor = (score) => {
    if (score >= 0.8) return '#CC3C3C';
    if (score >= 0.6) return '#CC983C';
    if (score >= 0.4) return '#CCB43C';
    if (score >= 0.2) return '#8FCC3C';
    return '#38CC38';
  };

  // ──────────────────────────────────────────────────────────
  // Описание слоя «priority-lines» с hover/selected
  // ──────────────────────────────────────────────────────────

  const riskLineLayer = {
    id: 'priority-lines',
    type: 'line',
    source: 'priority-segments',
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-color': [
        'case',
        ['any',
          ['boolean', ['feature-state', 'hover'], false],
          ['boolean', ['feature-state', 'selected'], false]
        ],
        [
          'case',
          ['>=', ['get', 'risk_score'], 0.8], getDarkRiskColor(0.8),
          ['>=', ['get', 'risk_score'], 0.6], getDarkRiskColor(0.6),
          ['>=', ['get', 'risk_score'], 0.4], getDarkRiskColor(0.4),
          ['>=', ['get', 'risk_score'], 0.2], getDarkRiskColor(0.2),
          getDarkRiskColor(0.0)
        ],
        [
          'case',
          ['>=', ['get', 'risk_score'], 0.8], getRiskColor(0.8),
          ['>=', ['get', 'risk_score'], 0.6], getRiskColor(0.6),
          ['>=', ['get', 'risk_score'], 0.4], getRiskColor(0.4),
          ['>=', ['get', 'risk_score'], 0.2], getRiskColor(0.2),
          getRiskColor(0.0)
        ]
      ],
      'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 6, 4],
      'line-opacity': 0.9
    }
  };

  // ──────────────────────────────────────────────────────────
  // Hover / click-обработчики для «priority-lines»
  // ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!map || !lineData.features.length) return;

    // Hover
    const onMove = (e) => {
      const feat = e.features?.[0];
      if (!feat || feat.id == null) return;
      const id = feat.id;
      if (hoveredCollectorId.current !== id) {
        if (hoveredCollectorId.current !== null) {
          map.setFeatureState(
            { source: 'priority-segments', id: hoveredCollectorId.current },
            { hover: false }
          );
        }
        hoveredCollectorId.current = id;
        map.setFeatureState({ source: 'priority-segments', id }, { hover: true });
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    // Leave
    const onLeave = () => {
      if (hoveredCollectorId.current !== null) {
        map.setFeatureState(
          { source: 'priority-segments', id: hoveredCollectorId.current },
          { hover: false }
        );
        hoveredCollectorId.current = null;
      }
      map.getCanvas().style.cursor = '';
    };

    // Click
    const onClick = (e) => {
      const feat = e.features?.[0];
      if (!feat || feat.id == null) return;
      const id = feat.id;

      // 1) Сбрасываем прошлый выбор (если он был и отличается от нового)
      if (selectedCollectorId.current !== null && selectedCollectorId.current !== id) {
        map.setFeatureState(
          { source: 'priority-segments', id: selectedCollectorId.current },
          { selected: false }
        );
      }
      // 2) Подсвечиваем новый выбранный
      map.setFeatureState({ source: 'priority-segments', id }, { selected: true });
      selectedCollectorId.current = id;

      // 3) Сохраняем в localStorage, чтобы при возврате панель осталась открытой
      localStorage.setItem('openedCollectorId', id);

      // 4) Находим коллектор c в merged-списке
      const coll = collectorsWithRisk.find(c => c.id === id);
      if (!coll) return;

      // 5) Собираем coords (для мини-карты, если потребуется)
      const feature = lineData.features.find(f => f.id === id);
      const flatCoords = feature.geometry.coordinates.flat();
      const coords = flatCoords.map(([lng, lat]) => ({ latitude: lat, longitude: lng }));

      // 6) Находим конструктивное решение по id
      const designObj = designs.find(d => d.id === coll.collector_design_id);
      const designName = designObj ? designObj.collector_design : '—';

      // 7) Открываем панель, передаём нужные поля
      setSelectedCollector({
        id: coll.id,
        name: coll.nameToShow,
        year: coll.year,
        design: designName,
        material: coll.material,
        length: coll.length,
        expenditure: coll.expenditure,
        risk_score: coll.risk_score,
        priority: coll.priority,
        coords
      });
      setAnimatePanel(true);
    };

    // Click вне линий → закрываем панель
    const onMapClick = (e) => {
      const hits = map.queryRenderedFeatures(e.point, {
        layers: ['priority-lines']
      });
      if (!hits.length) {
        if (selectedCollectorId.current !== null) {
          map.setFeatureState(
            { source: 'priority-segments', id: selectedCollectorId.current },
            { selected: false }
          );
        }
        // очищаем сохранённый id
        localStorage.removeItem('openedCollectorId');
        selectedCollectorId.current = null;
        setAnimatePanel(false);
        setTimeout(() => {
          setSelectedCollector(null);
        }, 250);
      }
    };

    map.on('mousemove', 'priority-lines', onMove);
    map.on('mouseleave', 'priority-lines', onLeave);
    map.on('click', 'priority-lines', onClick);
    map.on('click', onMapClick);

    return () => {
      map.off('mousemove', 'priority-lines', onMove);
      map.off('mouseleave', 'priority-lines', onLeave);
      map.off('click', 'priority-lines', onClick);
      map.off('click', onMapClick);
    };
  }, [map, lineData, collectorsWithRisk, designs]);

  // ──────────────────────────────────────────────────────────
  // Восстановление «открытого» коллектора при загрузке компонента
  // ──────────────────────────────────────────────────────────

  useEffect(() => {
    // Ждём, когда карта и данные загрузятся
    if (!map || !collectorsWithRisk.length) return;

    const storedId = Number(localStorage.getItem('openedCollectorId'));
    if (!storedId) return;

    // Проверяем, что такой коллектор есть в текущем списке
    const coll = collectorsWithRisk.find(c => c.id === storedId);
    if (!coll) {
      localStorage.removeItem('openedCollectorId');
      return;
    }

    // 1) Подсвечиваем линию: выставляем feature-state { selected: true }
    map.setFeatureState(
      { source: 'priority-segments', id: storedId },
      { selected: true }
    );
    selectedCollectorId.current = storedId;

    // 2) Собираем coords из lineData
    const feature = lineData.features.find(f => f.id === storedId);
    const flatCoords = feature.geometry.coordinates.flat();
    const coords = flatCoords.map(([lng, lat]) => ({ latitude: lat, longitude: lng }));

    // 3) Получаем конструктивное решение по collector_design_id
    const designObj = designs.find(d => d.id === coll.collector_design_id);
    const designName = designObj ? designObj.collector_design : '—';

    // 4) Открываем панель: сразу без «плавного» скрытого состояния
    setSelectedCollector({
      id: coll.id,
      name: coll.nameToShow,
      year: coll.year,
      design: designName,
      material: coll.material,
      length: coll.length,
      expenditure: coll.expenditure,
      risk_score: coll.risk_score,
      priority: coll.priority,
      coords
    });
    setAnimatePanel(true);
  }, [map, collectorsWithRisk, lineData, designs]);

  // ──────────────────────────────────────────────────────────
  // Helper для текстового лейбла риска
  // ──────────────────────────────────────────────────────────

  const riskLabel = (score) => {
    if (score >= 0.8) return 'Очень высокий';
    if (score >= 0.6) return 'Высокий';
    if (score >= 0.4) return 'Средний';
    if (score >= 0.2) return 'Низкий';
    return 'Очень низкий';
  };

  return (
    <>
      {/* Источник и слой «priority-segments» */}
      {map && lineData.features.length > 0 && (
        <Source id="priority-segments" type="geojson" data={lineData}>
          <Layer {...riskLineLayer} />
        </Source>
      )}

      {/* Боковая панель с информацией о выбранном коллекторе */}
      {selectedCollector && (
        <div className={'collector-info-panel' + (animatePanel ? ' open' : '')}>
          <button
            className="panel-close"
            onClick={() => {
              // 1) скрываем анимацию
              setAnimatePanel(false);
              // 2) по завершении transition убираем панель и очищаем localStorage
              setTimeout(() => {
                setSelectedCollector(null);
                localStorage.removeItem('openedCollectorId');
              }, 250);
            }}
          >
            ×
          </button>
          <div className="info-body">
            {/* Имя коллектора */}
            <div className="TTitle">{selectedCollector.name}</div>
            {/* Год ввода */}
            <div>
              <strong>Год ввода:</strong> {selectedCollector.year || '—'}
            </div>
            {/* Конструктивное решение */}
            <div>
              <strong>Конструктивное решение:</strong> {selectedCollector.design || '—'}
            </div>
            {/* Материал */}
            <div>
              <strong>Материал:</strong> {selectedCollector.material || '—'}
            </div>
            {/* Длина */}
            <div>
              <strong>Длина:</strong> {selectedCollector.length || '—'}
            </div>
            {/* Расход на участке */}
            <div>
              <strong>Расход на участке:</strong> {selectedCollector.expenditure || '—'}
            </div>

            <hr style={{ margin: '12px 0' }} />

            {/* Риск поломки (текст) */}
            <div>
              <strong>Риск поломки:</strong> {riskLabel(selectedCollector.risk_score)}
            </div>
            {/* Приоритет поломки (число) */}
            <div>
              <strong>Приоритет починки:</strong> {selectedCollector.priority}
            </div>

            <div className="guide-text" style={{ marginTop: '10px' }}>
              Для просмотра подробностей о коллекторе нажмите кнопку «Просмотр информации»
            </div>
            <button
              className="primary-btn"
              onClick={() =>
                navigate(`/collectors/${selectedCollector.id}`, {
                  state: { ...selectedCollector, source: 'priority' }
                })
              }
            >
              Просмотр информации &gt;
            </button>
          </div>
        </div>
      )}
    </>
  );
}
